import React, { useEffect, useMemo, useState } from "react";
import CreateIcon from "@mui/icons-material/Create";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { NavLink } from "react-router-dom";
import {
  deleteBatchViaAdmin,
  getAllBatchesViaAdmin,
  getPromotionCohortsViaAdmin,
  UpdateBatchViaAdmin,
} from "../../../utils/Api";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

const BatchesList = () => {
  const [allBatches, setAllBatches] = useState([]);
  const [cohorts, setCohorts] = useState([]);

  // Assign Cohort modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeBatch, setActiveBatch] = useState(null);
  const [assignCohortId, setAssignCohortId] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchAllBatches();
    fetchCohorts();
  }, [0]);

  const tableCustomStyles = {
    headRow: {
      style: {
        color: "#fff",
        backgroundColor: "#0F1015",
      },
    },
  };

  // Map cohort id -> name so the list can show the human-readable cohort per batch.
  const cohortNameById = useMemo(() => {
    const m = new Map();
    for (const c of cohorts) m.set(String(c._id), c.name);
    return m;
  }, [cohorts]);

  const columns = [
    {
      name: "SL. No",
      selector: (row, index) => ++index,
      width: "70px",
    },
    {
      name: "Batch Name",
      selector: (row) => row?.batchName,
      sortable: true,
      width: "150px",
    },
    {
      name: "Year Stage",
      selector: (row) => row?.yearStage || "-",
      sortable: true,
      width: "110px",
    },
    {
      name: "Division",
      selector: (row) => row?.division || "-",
      width: "100px",
    },
    {
      name: "Cohort",
      cell: (row) =>
        row?.cohort ? (
          <Chip
            label={cohortNameById.get(String(row.cohort)) || "Unknown"}
            size="small"
            sx={{ backgroundColor: "#e7f3ff", color: "#0b5ed7" }}
          />
        ) : (
          <span className="text-muted">—</span>
        ),
      width: "170px",
    },
    {
      name: "Term",
      selector: (row) => row?.term,
      sortable: true,
      width: "90px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex align-middle items-center gap-3">
          <button
            className="text-primary bg-none outline-none border-none"
            title="Assign Cohort"
            onClick={() => openAssignModal(row)}
          >
            <SwapHorizIcon />
          </button>
          <NavLink
            className="text-warning"
            title="Edit"
            to={`/panel-admin/edit-batch-form/${row._id}`}
          >
            <CreateIcon />
          </NavLink>
          <button
            className="text-danger bg-none outline-none border-none"
            title="Delete"
            onClick={(e) => deleteBatch(e, row._id)}
          >
            <DeleteForeverIcon />
          </button>
        </div>
      ),
    },
  ];

  const openAssignModal = (batch) => {
    setActiveBatch(batch);
    setAssignCohortId(batch?.cohort ? String(batch.cohort) : "");
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setActiveBatch(null);
    setAssignCohortId("");
  };

  // Partial update — send only { id, cohort }. The backend load-merge-save preserves every
  // other field and re-runs the duplicate-section guard against the merged doc.
  const handleAssign = async () => {
    if (!activeBatch) return;
    setAssigning(true);
    try {
      await UpdateBatchViaAdmin({
        id: activeBatch._id,
        cohort: assignCohortId || null,
      });
      toast.success(
        assignCohortId
          ? "Cohort assigned to batch"
          : "Cohort cleared from batch",
      );
      closeAssignModal();
      fetchAllBatches();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error assigning cohort");
    } finally {
      setAssigning(false);
    }
  };

  const deleteBatch = async (e, id) => {
    e.preventDefault();

    if (window.confirm("Are you sure you want to delete this batch?")) {
      try {
        const response = await deleteBatchViaAdmin({ id });
        console.log(`response:${response}`);
        toast.success("Batch Deleted...");
        fetchAllBatches();
      } catch (error) {
        console.log("Error ", error);
        toast.warning(
          error?.response?.data?.error || "Some Error happens Batch not Deleted...",
        );
      }
    }
  };

  const fetchAllBatches = async () => {
    try {
      const response = await getAllBatchesViaAdmin();
      setAllBatches(response.batches);
    } catch (error) {}
  };

  const fetchCohorts = async () => {
    try {
      const response = await getPromotionCohortsViaAdmin();
      setCohorts(response?.cohorts || []);
    } catch (error) {
      console.error("Error fetching cohorts:", error);
    }
  };

  return (
    <>
      <div>
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Batches</h4>
              <p className="card-description">All Batches are listed here...</p>
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={allBatches}
                  pagination={true}
                  customStyles={tableCustomStyles}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Assign Cohort Dialog — the recurring rollover step: re-point a slot at a new cohort. */}
        <Dialog open={showAssignModal} onClose={closeAssignModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <SwapHorizIcon color="primary" />
              <span>
                Assign Cohort{activeBatch ? `: ${activeBatch.batchName}` : ""}
              </span>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <p className="text-muted">
              The cohort is the intake group that occupies this batch slot. New admissions
              into this batch will join the selected cohort.
            </p>
            <div className="form-group">
              <label>Cohort</label>
              <select
                className="form-control p_input"
                value={assignCohortId}
                onChange={(e) => setAssignCohortId(e.target.value)}
              >
                <option value="">None</option>
                {cohorts.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {activeBatch?.yearStage === "FY" && !assignCohortId && (
              <p className="text-danger">
                This is a First-Year batch — leaving the cohort empty means new admissions
                into it will fail.
              </p>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeAssignModal} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleAssign}
              disabled={assigning}
              startIcon={assigning && <CircularProgress size={16} color="inherit" />}
            >
              {assigning ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default BatchesList;
